import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../utils/tpoGenQueryKeys';
import type { KpiBreakdownResponse, KpiBreakdownRow, MetricType } from '../../types';

interface UseKpiBreakdownArgs {
    planIds: string[];
    basePlanId: string | null;
    kpiKey: string | null;
    groupBy: string;
    metricType: MetricType;
    timePeriod?: 'full_year' | 'ytd' | 'ytg';
    search?: string;
}

const fetchKpiBreakdown = async (
    args: UseKpiBreakdownArgs,
    signal?: AbortSignal,
): Promise<KpiBreakdownResponse> => {
    let token = '';
    try { token = localStorage.getItem('accessToken') ?? ''; } catch {}
    const response = await axios(tpoGenUrls.getKpiTable, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        data: {
            plan_ids: args.planIds,
            base_plan_id: args.basePlanId,
            metric_type: args.metricType,
            group_by: [args.groupBy],
            kpi_list: [args.kpiKey],
            kpi_view: 'comparison',
            time_period: args.timePeriod ?? 'full_year',
            current_date: Math.floor(Date.now() / 1000),
            search_key: args.search || undefined,
        },
        signal,
    });
    return response.data;
};

interface UseKpiBreakdownDataResult {
    rows: KpiBreakdownRow[];
    totalCount: number;
    missingPlanIds: string[];
    isPending: boolean;
    isFetching: boolean;
    isError: boolean;
}

const useKpiBreakdownData = ({
    planIds,
    basePlanId,
    kpiKey,
    groupBy,
    metricType,
    timePeriod = 'full_year',
    search,
}: UseKpiBreakdownArgs): UseKpiBreakdownDataResult => {
    const enabled = planIds.length > 0 && kpiKey != null && basePlanId != null;

    const { data, isPending, isFetching, isError } = useQuery({
        queryKey: tpoGenKeys.kpiBreakdown(
            planIds,
            basePlanId ?? '',
            kpiKey ?? '',
            groupBy,
            metricType,
            timePeriod,
            search,
        ),
        queryFn: ({ signal }) => fetchKpiBreakdown(
            { planIds, basePlanId, kpiKey, groupBy, metricType, timePeriod, search },
            signal,
        ),
        enabled,
    });

    return {
        rows: data?.data ?? [],
        totalCount: data?.total_count ?? 0,
        missingPlanIds: data?.missing_plan_ids ?? [],
        isPending: enabled && isPending,
        isFetching,
        isError,
    };
};

export default useKpiBreakdownData;
